import {useState} from 'react';
import {Check, X} from 'lucide-react';
import Select from '@/matic/components/ui/select';
import type {FileCategory} from '../types/files-types';

const NEW_CATEGORY_VALUE = '__new_file_category__';

type TagPickerProps = {
  value: string | null;
  onChange: (id: string | null) => void;
  categories: FileCategory[];
  onCreateCategory?: (name: string, sortOrder: number) => Promise<FileCategory>;
  isCreatePending?: boolean;
};

export function TagPicker({
  value,
  onChange,
  categories,
  onCreateCategory,
  isCreatePending = false,
}: TagPickerProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const activeCategories = categories.filter((c) => c.deletedAt === null);
  const options = [
    {value: '', label: 'No tag'},
    ...activeCategories.map((cat) => ({value: cat.id, label: cat.name})),
    ...(onCreateCategory
      ? [{value: NEW_CATEGORY_VALUE, label: '+ New tag...'}]
      : []),
  ];

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed || !onCreateCategory) return;

    try {
      const created = await onCreateCategory(trimmed, activeCategories.length);
      onChange(created.id);
      setNewName('');
      setIsCreating(false);
    } catch {
      // The mutation hook owns the toast; keep the inline creator open.
    }
  };

  return (
    <div>
      <Select
        options={options}
        value={value ?? ''}
        onChange={(e) => {
          if (e.target.value === NEW_CATEGORY_VALUE) {
            setIsCreating(true);
            return;
          }

          onChange(e.target.value === '' ? null : e.target.value);
        }}
      />
      {isCreating && (
        <div className="mt-2 flex items-center gap-1.5">
          <input
            autoFocus
            type="text"
            value={newName}
            onChange={(event) => {
              setNewName(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleCreate();
              }

              if (event.key === 'Escape') {
                setIsCreating(false);
                setNewName('');
              }
            }}
            className="min-w-0 flex-1 rounded-md border border-neutral-200 bg-white px-2.5 py-1.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="New tag name"
          />
          <button
            type="button"
            title="Create tag"
            disabled={!newName.trim() || isCreatePending}
            className="rounded p-1.5 text-neutral-500 hover:bg-success-50 hover:text-success-700 disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => {
              void handleCreate();
            }}
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            title="Cancel"
            className="rounded p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
            onClick={() => {
              setIsCreating(false);
              setNewName('');
            }}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
